import React, { useState } from 'react';
import JobCard from './JobCard';
import { FaFilter, FaBuilding, FaCode, FaMoneyBillWave, FaLaptopHouse, FaTimes, FaSearch } from 'react-icons/fa';

const JobsList = ({ jobs, onApply }) => {
  const [filters, setFilters] = useState({
    role: '',
    company: '',
    mode: '',
    salaryRange: ''
  });

  // Get unique values for filter options
  const roles = [...new Set(jobs.map(job => job.role))];
  const companies = [...new Set(jobs.map(job => job.company))];
  const modes = [...new Set(jobs.map(job => job.mode))];
  const salaryRanges = [...new Set(jobs.map(job => job.salaryRange))];

  const filteredJobs = jobs.filter(job => {
    const matchRole = !filters.role || job.role === filters.role;
    const matchCompany = !filters.company || job.company === filters.company;
    const matchMode = !filters.mode || job.mode === filters.mode;
    const matchSalary = !filters.salaryRange || job.salaryRange === filters.salaryRange;

    return matchRole && matchCompany && matchMode && matchSalary;
  });

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  return (
    <div className="flex gap-8">
      {/* Enhanced Filter Sidebar */}
      <div className="w-80 bg-white rounded-2xl shadow-md p-8 h-fit sticky top-8 border border-gray-100">
        <div className="flex items-center space-x-3 mb-8 pb-4 border-b border-gray-100">
          <FaFilter className="text-indigo-600 text-xl" />
          <h2 className="text-xl font-bold text-gray-800">Filter Jobs</h2>
        </div>

        {/* Role Filter - Enhanced styling */}
        <div className="mb-8">
          <div className="flex items-center space-x-2 mb-3">
            <FaCode className="text-indigo-600" />
            <h3 className="font-semibold text-gray-700">Role</h3>
          </div>
          <select
            className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl
                       focus:ring-2 focus:ring-indigo-100 focus:border-indigo-300
                       text-gray-600 appearance-none cursor-pointer
                       transition-all duration-200 hover:bg-gray-100"
            value={filters.role}
            onChange={(e) => handleFilterChange('role', e.target.value)}
          >
            <option value="">All Roles</option>
            {roles.map(role => (
              <option key={role} value={role}>{role}</option>
            ))}
          </select>
        </div>

        {/* Company Filter */}
        <div className="mb-8">
          <div className="flex items-center space-x-2 mb-3">
            <FaBuilding className="text-indigo-600" />
            <h3 className="font-semibold text-gray-700">Company</h3>
          </div>
          <select
            className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl
                       focus:ring-2 focus:ring-indigo-100 focus:border-indigo-300
                       text-gray-600 appearance-none cursor-pointer
                       transition-all duration-200 hover:bg-gray-100"
            value={filters.company}
            onChange={(e) => handleFilterChange('company', e.target.value)}
          >
            <option value="">All Companies</option>
            {companies.map(company => (
              <option key={company} value={company}>{company}</option>
            ))}
          </select>
        </div>

        {/* Work Mode Filter */}
        <div className="mb-8">
          <div className="flex items-center space-x-2 mb-3">
            <FaLaptopHouse className="text-indigo-600" />
            <h3 className="font-semibold text-gray-700">Work Mode</h3>
          </div>
          <select
            className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl
                       focus:ring-2 focus:ring-indigo-100 focus:border-indigo-300
                       text-gray-600 appearance-none cursor-pointer
                       transition-all duration-200 hover:bg-gray-100"
            value={filters.mode}
            onChange={(e) => handleFilterChange('mode', e.target.value)}
          >
            <option value="">All Modes</option>
            {modes.map(mode => (
              <option key={mode} value={mode}>{mode}</option>
            ))}
          </select>
        </div>

        {/* Salary Range Filter */}
        <div className="mb-8">
          <div className="flex items-center space-x-2 mb-3">
            <FaMoneyBillWave className="text-indigo-600" />
            <h3 className="font-semibold text-gray-700">Salary Range</h3>
          </div>
          <select
            className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl
                       focus:ring-2 focus:ring-indigo-100 focus:border-indigo-300
                       text-gray-600 appearance-none cursor-pointer
                       transition-all duration-200 hover:bg-gray-100"
            value={filters.salaryRange}
            onChange={(e) => handleFilterChange('salaryRange', e.target.value)}
          >
            <option value="">All Ranges</option>
            {salaryRanges.map(range => (
              <option key={range} value={range}>{range}</option>
            ))}
          </select>
        </div>

        {/* Clear Filters Button - Enhanced styling */}
        <button
          onClick={() => setFilters({ role: '', company: '', mode: '', salaryRange: '' })}
          className="w-full py-3 px-4 bg-white text-indigo-600 border-2 border-indigo-600 
                    rounded-xl font-semibold hover:bg-indigo-50 active:bg-indigo-100
                    transition-all duration-200 flex items-center justify-center space-x-2"
        >
          <FaTimes className="text-sm" />
          <span>Clear All Filters</span>
        </button>

        {/* Active Filters Count */}
        <div className="mt-6 text-center text-sm text-gray-500">
          {Object.values(filters).filter(Boolean).length} filters active
        </div>
      </div>

      {/* Jobs Grid */}
      <div className="flex-1">
        {filteredJobs.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {filteredJobs.map(job => (
              <JobCard key={job.id} job={job} onApply={onApply} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-gray-50 rounded-2xl border border-gray-200">
            <FaSearch className="mx-auto text-4xl text-gray-400 mb-4" />
            <p className="text-gray-600 font-medium">No jobs match your selected filters</p>
            <p className="text-gray-500 text-sm mt-2">Try adjusting your filters to see more results</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default JobsList;